import { useAllFieldResponses } from "@client/hooks/use-field-responses";
import { useActingAsUserId, useRound } from "@client/pages/round/round-context";
import {
  attachWorkerErrorReporting,
  reportWorkerProtocolError,
} from "@client/telemetry/workerErrorReporting";
import { isOfficiallyRecused } from "@client/utils/interest";
import { isRichTextBlank } from "@client/utils/isRichTextBlank";
import {
  getActiveProposals,
  getEvaluableRequestBase,
  getRecommenderFromUser,
  getUnevaluatedEvaluableGroups,
  isUserFunder,
  isUserRecommender,
} from "@engine/round/accessors";
import type {
  EvaluationFinality,
  EvaluationState,
  Proposal,
  Recommender,
  RoundAggregate,
  RoundMember,
} from "@engine/round/aggregate";
import {
  getInvestigation,
  getProposalDisclosure,
  getReviewProgress,
} from "@engine/round/proposalMemberData";
import type { Evaluable, Track } from "@lib/types";
import MiniSearch, { type SearchResult } from "minisearch";
import {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  PROPOSAL_MINISEARCH_OPTIONS,
  PROPOSAL_MINISEARCH_QUERY_OPTIONS,
  type ProposalSearchDocument as ProposalSearchWorkerDocument,
  type ProposalSearchHit,
  type ProposalSearchWorkerRequest,
  type ProposalSearchWorkerResponse,
} from "./proposal-search/proposalSearchTypes";
import { useSearchParamsWithoutEmptyQuery } from "./use-search-params-without-empty-query";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Track category for checkbox filtering. */
export type TrackCategory = "both" | "freedom" | "fairness" | "none";

export const ALL_TRACK_CATEGORIES: TrackCategory[] = [
  "both",
  "freedom",
  "fairness",
  "none",
];

/** Sort options — used by the page and filter bar, not by this hook. */
export type SortOption =
  | "search-relevance"
  | "last-touched"
  | "base-request"
  | "org-name"
  | "status"
  | "investigation-interest"
  | "review-progress"
  | "notes";

export type SortDirection = "asc" | "desc";

export const ALL_STATUSES: EvaluationState[] = [
  "not-started",
  "evaluated",
  "canceled",
  "recused",
  "deferred",
];

/** Single evaluation row for the table. */
export interface EvaluationRow {
  proposalId: string;
  shortname: string;
  longname: string;
  tracks: Track[];
  baseRequest: number | null;
  status: EvaluationState;
  engineState: EvaluationState;
  /**
   * The recommender's final verdict on this proposal, if they recorded one.
   * Carried alongside `status` rather than folded into it — `EvaluationState`
   * is an engine state machine, and finality is an independent annotation.
   */
  finality: EvaluationFinality | null;
  /** The recommender's standing "this is harmful" claim, independent of both. */
  negativeValue: boolean;
  lastTouched: string | null;
  investigationInterest: number | null;
  reviewProgress: number | null;
  isRecused: boolean;
  hasViewerNotes: boolean;
  proposal: Proposal;
  hidden?: boolean;
  searchScore?: number;
  searchMatchPreview?: ProposalSearchMatchPreview;
}

export interface ProposalSearchMatchPreview {
  label: string;
  before: string;
  match: string;
  after: string;
}

/** Counts by status. */
export interface StatusCounts {
  allActive: number;
  allInactive: number;
  hidden: number;
  notStarted: number;
  evaluated: number;
  canceled: number;
  recused: number;
  deferred: number;
  total: number;
}

/** StatusCounts with nullable fields, used for skeleton/loading states. */
export type SkeletonStatusCounts = {
  [K in keyof StatusCounts]: number | null;
};

/** Progress data. */
export interface ProgressData {
  completed: number;
  total: number;
  milestoneLabel?: string;
  targetPercent?: number;
  targetLabel?: string;
}

/** ProgressData with nullable fields, used for skeleton/loading states. */
export interface SkeletonProgressData {
  completed: number | null;
  total: number | null;
  milestoneLabel?: string;
  targetPercent?: number;
  targetLabel?: string;
}

/** Return type of the hook. */
export interface MyEvaluationsData {
  /** The current recommender (null if not a recommender). */
  recommender: Recommender | null;
  /** True when the current viewer is an active round member with no active evaluator role. */
  isObserver: boolean;

  /** All evaluation rows (unfiltered). */
  allRows: EvaluationRow[];
  /** Filtered rows (not sorted — sorting is handled by the table). */
  filteredRows: EvaluationRow[];

  /** Counts by status (from all rows, not filtered). */
  counts: StatusCounts;
  /** Progress data. */
  progress: ProgressData;

  /** Current track filter. Empty array = no filter (all tracks shown). */
  trackFilter: TrackCategory[];
  setTrackFilter: (filter: TrackCategory[]) => void;

  /** Current status filter. Empty array = no filter (all statuses shown). */
  statusFilter: EvaluationState[];
  setStatusFilter: (filter: EvaluationState[]) => void;

  /** Whether to show hidden proposals only. */
  showHidden: boolean;
  setShowHidden: (show: boolean) => void;

  /** Current search query (urgent — tracks the input value). */
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  /**
   * The search query the current `filteredRows`/`searchScore` actually reflect.
   * Sort gating must key off this (not `searchQuery`) so the chosen sort never
   * diverges from the rows and scores it's sorting — during the deferred window
   * or while the search worker is answering a freshly-typed query.
   */
  appliedSearchQuery: string;

  /** True when any filter is narrowing the list below its defaults. */
  hasActiveFilters: boolean;
  /** Reset track, status, visibility, and search back to defaults. */
  clearFilters: () => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const CONTEXT_BEFORE_MATCH = 120;
const CONTEXT_AFTER_MATCH = 280;

type ProposalSearchDocument = {
  id: string;
  proposalId: string;
  label: string;
  title: string;
  text: string;
};

type ProposalSearchResult = SearchResult & {
  proposalId: string;
  label: string;
  title: string;
  text: string;
};

/** Get tracks array from proposal typed fields. */
function getProposalTracks(proposal: Proposal): Track[] {
  const tracks: Track[] = [];
  if (proposal.typedFields.targetsFreedom) {
    tracks.push("FREEDOM");
  }
  if (proposal.typedFields.targetsFairness) {
    tracks.push("FAIRNESS");
  }
  return tracks;
}

/** Build evaluation rows from round data. */
function buildEvaluationRows(
  round: RoundAggregate,
  recommender: Recommender,
  memberId: string,
  viewerMember: RoundMember | undefined,
): EvaluationRow[] {
  const groups = getUnevaluatedEvaluableGroups(round, recommender);

  const rows: EvaluationRow[] = [];

  const getMemberData = (proposalId: string) => {
    const disclosure = getProposalDisclosure(round, proposalId, memberId);
    return {
      investigationInterest:
        getInvestigation(round, proposalId, memberId)?.interest ?? null,
      reviewProgress: getReviewProgress(round, proposalId, memberId) ?? null,
      isRecused: isOfficiallyRecused(disclosure),
    };
  };

  const addFromGroup = (
    proposals: Evaluable[],
    status: EvaluationState,
    isHiddenRow = false,
  ) => {
    for (const evaluable of proposals) {
      if (evaluable.type !== "proposal") {
        throw new Error(
          `Expected Proposal in evaluation group, got ${evaluable.type}`,
        );
      }
      const proposal = evaluable;
      if (proposal.id === round.holdProposalId) {
        continue;
      }
      const evaluation = recommender.evaluations[proposal.id];
      rows.push({
        proposalId: proposal.id,
        shortname: proposal.shortname,
        longname: proposal.longname,
        tracks: getProposalTracks(proposal),
        baseRequest: getEvaluableRequestBase(round, proposal.id),
        status,
        engineState: status,
        finality: evaluation?.finality ?? null,
        negativeValue: evaluation?.negativeValue === true,
        lastTouched: evaluation?.lastTouchedAt ?? null,
        ...getMemberData(proposal.id),
        hasViewerNotes: hasSavedNote(viewerMember, proposal.id),
        proposal,
        ...(isHiddenRow ? { hidden: true as const } : {}),
      });
    }
  };

  addFromGroup(groups.evaluated, "evaluated");
  addFromGroup(groups.zeroed, "evaluated");
  addFromGroup(groups.deferred, "deferred");
  addFromGroup(groups.notStarted, "not-started");
  addFromGroup(groups.recused, "recused");
  addFromGroup(groups.canceled, "canceled");

  for (const evaluable of groups.hidden) {
    if (evaluable.type !== "proposal") {
      throw new Error(
        `Expected Proposal in evaluation group, got ${evaluable.type}`,
      );
    }
    const proposal = evaluable;
    if (proposal.id === round.holdProposalId) {
      continue;
    }
    const evaluation = recommender.evaluations[proposal.id];
    const state = evaluation?.state ?? "not-started";
    rows.push({
      proposalId: proposal.id,
      shortname: proposal.shortname,
      longname: proposal.longname,
      tracks: getProposalTracks(proposal),
      baseRequest: getEvaluableRequestBase(round, proposal.id),
      status: state,
      engineState: state,
      finality: evaluation?.finality ?? null,
      negativeValue: evaluation?.negativeValue === true,
      lastTouched: evaluation?.lastTouchedAt ?? null,
      ...getMemberData(proposal.id),
      hasViewerNotes: hasSavedNote(viewerMember, proposal.id),
      proposal,
      hidden: true,
    });
  }

  return rows;
}

/** Build proposal rows for non-recommenders. */
function buildProposalRows(
  round: RoundAggregate,
  viewerMember: RoundMember | undefined,
): EvaluationRow[] {
  return getActiveProposals(round)
    .filter((proposal) => proposal.id !== round.holdProposalId)
    .map((proposal) => ({
      proposalId: proposal.id,
      shortname: proposal.shortname,
      longname: proposal.longname,
      tracks: getProposalTracks(proposal),
      baseRequest: getEvaluableRequestBase(round, proposal.id),
      status: "not-started" as const,
      engineState: "not-started" as const,
      // Non-recommender viewers have no evaluation of their own to annotate;
      // they read recommenders' verdicts on the proposal page instead.
      finality: null,
      negativeValue: false,
      lastTouched: null,
      investigationInterest: null,
      reviewProgress: null,
      isRecused: false,
      hasViewerNotes: hasSavedNote(viewerMember, proposal.id),
      proposal,
    }));
}

function hasSavedNote(
  member: RoundMember | undefined,
  proposalId: string,
): boolean {
  return !isRichTextBlank(member?.evaluableNotes[proposalId]);
}

export function isRoundObserver(
  round: RoundAggregate,
  userId: string,
): boolean {
  const member = round.members[userId];
  return (
    member?.status === "active" &&
    !isUserRecommender(round, userId) &&
    !isUserFunder(round, userId)
  );
}

/** Get the track category for a row. */
export function getTrackCategory(row: EvaluationRow): TrackCategory {
  if (row.tracks.length === 2) return "both";
  if (row.tracks.includes("FREEDOM")) return "freedom";
  if (row.tracks.includes("FAIRNESS")) return "fairness";
  return "none";
}

type ProposalFieldTitlesByProposalId = Record<string, Record<string, string>>;

function normalizeSearchText(value: string): string {
  return value.replace(/\s+/g, " ").trim();
}

function getMatchingSnippetForTerm(
  label: string,
  value: string | null | undefined,
  terms: string[],
): ProposalSearchMatchPreview | null {
  if (!value) return null;

  const text = normalizeSearchText(value);
  const lowerText = text.toLowerCase();
  const match = terms
    .map((term) => {
      const matchIndex = lowerText.indexOf(term.toLowerCase());
      return matchIndex === -1 ? null : { term, matchIndex };
    })
    .filter(
      (entry): entry is { term: string; matchIndex: number } => entry != null,
    )
    .sort((a, b) => a.matchIndex - b.matchIndex)[0];
  if (!match) return null;

  const start = Math.max(0, match.matchIndex - CONTEXT_BEFORE_MATCH);
  const end = Math.min(
    text.length,
    match.matchIndex + match.term.length + CONTEXT_AFTER_MATCH,
  );

  return {
    label,
    before: `${start > 0 ? "..." : ""}${text.slice(start, match.matchIndex)}`,
    match: text.slice(match.matchIndex, match.matchIndex + match.term.length),
    after: `${text.slice(match.matchIndex + match.term.length, end)}${
      end < text.length ? "..." : ""
    }`,
  };
}

/**
 * Cheap content fingerprint (djb2) the search worker diffs against the indexed
 * copy of a document, so re-extractions only re-index what actually changed.
 * Covers every stored field that can change independently of the document id
 * (`label`, `title`, `text`): `label` matters because renaming a form question
 * changes the preview's label without touching the answer, and the worker would
 * otherwise skip the update and keep showing the old question name.
 */
function fingerprintSearchDocument(
  document: Omit<ProposalSearchWorkerDocument, "version">,
): string {
  let hash = 5381;
  const combined = `${document.label} ${document.title} ${document.text}`;
  for (let i = 0; i < combined.length; i++) {
    hash = ((hash << 5) + hash) ^ combined.charCodeAt(i);
  }
  return `${combined.length}:${(hash >>> 0).toString(36)}`;
}

function addProposalSearchDocument(
  documents: ProposalSearchWorkerDocument[],
  document: Omit<ProposalSearchWorkerDocument, "version">,
) {
  if (normalizeSearchText(document.text).length === 0) return;
  documents.push({
    ...document,
    version: fingerprintSearchDocument(document),
  });
}

/**
 * Extract the searchable documents (one per proposal title + one per field
 * response) from the current rows and field responses. Pure — shared by the
 * worker-fed pipeline and the synchronous fallback below.
 */
function buildProposalSearchDocuments(
  rows: EvaluationRow[],
  fieldResponsesByProposalId: Record<string, Record<string, string>>,
  fieldTitlesByProposalId: ProposalFieldTitlesByProposalId,
): ProposalSearchWorkerDocument[] {
  const documents: ProposalSearchWorkerDocument[] = [];

  for (const row of rows) {
    addProposalSearchDocument(documents, {
      id: `proposal:${row.proposalId}:title`,
      proposalId: row.proposalId,
      label: "Proposal",
      title: `${row.shortname} ${row.longname}`,
      text: [row.shortname, row.longname].join("\n"),
    });

    const fieldResponses = fieldResponsesByProposalId[row.proposalId] ?? {};
    const fieldTitles = fieldTitlesByProposalId[row.proposalId] ?? {};
    for (const [fieldId, value] of Object.entries(fieldResponses)) {
      addProposalSearchDocument(documents, {
        id: `proposal:${row.proposalId}:field:${fieldId}`,
        proposalId: row.proposalId,
        label: fieldTitles[fieldId] ?? "Proposal field",
        title: `${row.shortname} ${row.longname}`,
        text: value,
      });
    }
  }

  return documents;
}

function buildProposalSearchIndex(
  rows: EvaluationRow[],
  fieldResponsesByProposalId: Record<string, Record<string, string>>,
  fieldTitlesByProposalId: ProposalFieldTitlesByProposalId,
): MiniSearch<ProposalSearchDocument> {
  const index = new MiniSearch<ProposalSearchDocument>(
    PROPOSAL_MINISEARCH_OPTIONS,
  );
  index.addAll(
    buildProposalSearchDocuments(
      rows,
      fieldResponsesByProposalId,
      fieldTitlesByProposalId,
    ),
  );
  return index;
}

type ProposalSearchMatch = {
  score: number;
  preview: ProposalSearchMatchPreview | undefined;
};

/**
 * The fields the scorer/snippet need from a search result. Both an in-process
 * `ProposalSearchResult` and a worker `ProposalSearchHit` satisfy this, so the
 * scoring path is shared regardless of where MiniSearch ran.
 */
type ScorableProposalResult = {
  proposalId: string;
  label: string;
  title: string;
  text: string;
  terms: string[];
  score: number;
};

function boostProposalSearchScore(
  result: ScorableProposalResult,
  searchQuery: string,
): number {
  const query = normalizeSearchText(searchQuery).toLowerCase();
  const title = normalizeSearchText(result.title).toLowerCase();
  const text = normalizeSearchText(result.text).toLowerCase();
  const termMatchesTitle = result.terms.some((term) =>
    title.includes(term.toLowerCase()),
  );

  let multiplier = 1;
  if (title.includes(query)) {
    multiplier *= 4;
  } else if (termMatchesTitle) {
    multiplier *= 2;
  }
  if (text.includes(query)) {
    multiplier *= 1.5;
  }
  return result.score * multiplier;
}

/**
 * Reduce raw MiniSearch results (from either the in-process index or the
 * worker) to the best match per proposal, attaching the boosted score and a
 * snippet. Shared by the synchronous fallback and the worker pipeline so the
 * scoring/snippet behavior is identical regardless of where search ran.
 */
function reduceProposalSearchResults(
  results: ScorableProposalResult[],
  searchQuery: string,
): Map<string, ProposalSearchMatch> {
  const matchesByProposalId = new Map<string, ProposalSearchMatch>();

  for (const result of results) {
    const match: ProposalSearchMatch = {
      score: boostProposalSearchScore(result, searchQuery),
      preview:
        getMatchingSnippetForTerm(result.label, result.text, result.terms) ??
        undefined,
    };
    const existing = matchesByProposalId.get(result.proposalId);
    if (existing != null && existing.score >= match.score) continue;
    matchesByProposalId.set(result.proposalId, match);
  }

  return matchesByProposalId;
}

function searchProposals(
  searchIndex: MiniSearch<ProposalSearchDocument>,
  searchQuery: string,
): Map<string, ProposalSearchMatch> {
  const results = searchIndex.search(
    normalizeSearchText(searchQuery),
    PROPOSAL_MINISEARCH_QUERY_OPTIONS,
  ) as ProposalSearchResult[];
  return reduceProposalSearchResults(results, searchQuery);
}

/** Attach search scores/previews to the rows that matched, dropping the rest. */
function applyProposalSearchMatches(
  rows: EvaluationRow[],
  matchesByProposalId: Map<string, ProposalSearchMatch>,
): EvaluationRow[] {
  return rows.flatMap((row) => {
    const match = matchesByProposalId.get(row.proposalId);
    if (match == null) return [];
    return [
      {
        ...row,
        searchScore: match.score,
        searchMatchPreview: match.preview,
      },
    ];
  });
}

function buildFieldTitlesByProposalId(
  round: RoundAggregate,
): ProposalFieldTitlesByProposalId {
  const fieldTitlesByProposalId: ProposalFieldTitlesByProposalId = {};

  for (const proposalForm of Object.values(round.proposalForms)) {
    const fieldTitles = Object.fromEntries(
      proposalForm.fields.map((field) => [field.id, field.title]),
    );
    for (const proposalId of Object.keys(proposalForm.proposals)) {
      fieldTitlesByProposalId[proposalId] = fieldTitles;
    }
  }

  return fieldTitlesByProposalId;
}

/** Apply filters to rows (sorting is handled by the table).
 *
 * Selection semantics (Model B — "empty = no filter"):
 * - Empty `trackCategories` / `statusCategories`  → that dimension is unfiltered.
 * - Non-empty                                     → only rows matching the selection pass.
 *
 * A full selection (all categories/statuses selected) is indistinguishable
 * from no selection and produces the same result.
 */
export function filterRows(
  rows: EvaluationRow[],
  trackCategories: TrackCategory[],
  statusCategories: EvaluationState[],
  showHidden: boolean,
  searchQuery: string,
  fieldResponsesByProposalId: Record<string, Record<string, string>> = {},
  fieldTitlesByProposalId: ProposalFieldTitlesByProposalId = {},
  proposalSearchIndex?: MiniSearch<ProposalSearchDocument>,
): EvaluationRow[] {
  let filtered = showHidden
    ? rows.filter((row) => row.hidden === true)
    : rows.filter((row) => row.hidden !== true);

  if (statusCategories.length > 0) {
    filtered = filtered.filter((row) => statusCategories.includes(row.status));
  }

  if (trackCategories.length > 0) {
    filtered = filtered.filter((row) =>
      trackCategories.includes(getTrackCategory(row)),
    );
  }

  if (searchQuery.trim()) {
    const searchIndex =
      proposalSearchIndex ??
      buildProposalSearchIndex(
        rows,
        fieldResponsesByProposalId,
        fieldTitlesByProposalId,
      );
    const matchesByProposalId = searchProposals(searchIndex, searchQuery);
    filtered = applyProposalSearchMatches(filtered, matchesByProposalId);
  }

  return filtered;
}

/** Calculate counts from all rows. */
export function calculateCounts(rows: EvaluationRow[]): StatusCounts {
  let hidden = 0;
  let notStarted = 0;
  let evaluated = 0;
  let canceled = 0;
  let recused = 0;
  let deferred = 0;

  for (const row of rows) {
    if (row.hidden === true) {
      hidden++;
      continue;
    }
    switch (row.status) {
      case "not-started":
        notStarted++;
        break;
      case "evaluated":
        evaluated++;
        break;
      case "canceled":
        canceled++;
        break;
      case "recused":
        recused++;
        break;
      case "deferred":
        deferred++;
        break;
    }
  }

  const total = rows.length - hidden;
  const allActive = notStarted + evaluated;
  const allInactive = canceled + recused + deferred;

  return {
    allActive,
    allInactive,
    hidden,
    notStarted,
    evaluated,
    canceled,
    recused,
    deferred,
    total,
  };
}

// ---------------------------------------------------------------------------
// URL search param helpers
// ---------------------------------------------------------------------------

const VALID_TRACK_CATEGORIES = new Set<string>(ALL_TRACK_CATEGORIES);
const VALID_STATUSES = new Set<string>(ALL_STATUSES);

/** Parse a comma-separated filter param.
 *
 * `[]` (from a missing, empty, or all-invalid param) means "no filter." A
 * fully-selected param stays full — we deliberately *don't* normalize it
 * down to `[]`, because that would silently uncheck every box the user
 * just clicked. The two states produce identical results; the UI derives
 * "Any"/inactive display from them (see `hasActiveFilters`).
 */
function parseTrackFilter(param: string | null): TrackCategory[] {
  if (!param) return [];
  return param
    .split(",")
    .filter((v) => VALID_TRACK_CATEGORIES.has(v)) as TrackCategory[];
}

function parseStatusFilter(param: string | null): EvaluationState[] {
  if (!param) return [];
  return param
    .split(",")
    .filter((v) => VALID_STATUSES.has(v)) as EvaluationState[];
}

/** Serialize to a URL param. Only `[]` collapses to null (the default).
 * Non-empty selections — including full ones — are preserved verbatim so
 * the user's state round-trips through the URL unchanged. */
function serializeFilterParam<T extends string>(values: T[]): string | null {
  if (values.length === 0) return null;
  return values.join(",");
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Centralized hook for Proposals page data.
 * Provides filtered rows and filter state backed by URL search params.
 * Sorting is handled by the table.
 */
export function useMyEvaluationsData(): MyEvaluationsData {
  const round = useRound();
  const userId = useActingAsUserId();
  const fieldResponsesByProposalId = useAllFieldResponses();
  const [searchParams, setSearchParams] = useSearchParamsWithoutEmptyQuery();

  // Get current recommender
  const recommender = useMemo(
    () => getRecommenderFromUser(round, userId),
    [round, userId],
  );
  const viewerMember = round.members[userId];
  const isObserver = useMemo(
    () => isRoundObserver(round, userId),
    [round, userId],
  );

  // Read filter state from URL
  const trackFilter = useMemo(
    () => parseTrackFilter(searchParams.get("track")),
    [searchParams],
  );
  const statusFilter = useMemo(
    () => parseStatusFilter(searchParams.get("status")),
    [searchParams],
  );
  const showHidden = searchParams.get("hidden") === "1";

  // Search uses local state for instant filtering; the URL is updated on a
  // debounce so typing doesn't round-trip through wouter on every keystroke.
  const urlSearchQuery = searchParams.get("q") ?? "";
  const [searchQuery, setSearchQueryLocal] = useState(urlSearchQuery);

  // Adopt URL changes that didn't originate here (clear filters, browser nav).
  useEffect(() => {
    setSearchQueryLocal(urlSearchQuery);
  }, [urlSearchQuery]);

  // Uses wouter's callback form of setSearchParams so consecutive calls
  // in the same event handler don't clobber each other (each sees the
  // previous call's result, not the stale pre-render snapshot).
  const updateParams = useCallback(
    (
      updates: Record<string, string | null>,
      options?: { replace?: boolean },
    ) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(updates)) {
          if (value == null) {
            next.delete(key);
          } else {
            next.set(key, value);
          }
        }
        return next;
      }, options);
    },
    [setSearchParams],
  );

  const setTrackFilter = useCallback(
    (filter: TrackCategory[]) => {
      updateParams({ track: serializeFilterParam(filter) });
    },
    [updateParams],
  );

  const setStatusFilter = useCallback(
    (filter: EvaluationState[]) => {
      updateParams({ status: serializeFilterParam(filter) });
    },
    [updateParams],
  );

  const setShowHidden = useCallback(
    (show: boolean) => {
      updateParams({ hidden: show ? "1" : null });
    },
    [updateParams],
  );

  // Update local state immediately (so the input + filter stay snappy) and
  // debounce the URL write — that's the part that re-runs wouter's params
  // hook tree-wide. `replace: true` keeps each keystroke out of history.
  const setSearchQuery = useCallback((query: string) => {
    setSearchQueryLocal(query);
  }, []);

  useEffect(() => {
    if (searchQuery === urlSearchQuery) return;
    const timer = setTimeout(() => {
      updateParams({ q: searchQuery || null }, { replace: true });
    }, 200);
    return () => clearTimeout(timer);
  }, [searchQuery, urlSearchQuery, updateParams]);

  const clearFilters = useCallback(() => {
    // Reset local search synchronously — the pending debounce would otherwise
    // write stale input back into `q` after the URL has already been cleared,
    // and the adopt-URL effect can't catch it when `urlSearchQuery` stays "".
    setSearchQueryLocal("");
    updateParams({ track: null, status: null, hidden: null, q: null });
  }, [updateParams]);

  // Non-recommenders shouldn't have status/hidden filters — clear if present
  const effectiveStatusFilter = !recommender ? [] : statusFilter;
  const effectiveShowHidden = !recommender ? false : showHidden;

  // Build all evaluation rows
  const allRows = useMemo(() => {
    if (recommender) {
      return buildEvaluationRows(round, recommender, userId, viewerMember);
    }
    return buildProposalRows(round, viewerMember);
  }, [round, recommender, userId, viewerMember]);

  // Calculate counts (from all rows, before filtering)
  const counts = useMemo(() => calculateCounts(allRows), [allRows]);
  const fieldTitlesByProposalId = useMemo(
    () => buildFieldTitlesByProposalId(round),
    [round],
  );
  // The input value tracks `searchQuery` directly (urgent), while filtering
  // uses the deferred value so React can prioritize keystrokes over the
  // potentially-heavy table re-render that follows.
  const deferredSearchQuery = useDeferredValue(searchQuery);

  // Building the MiniSearch index over every proposal + field response costs
  // ~300ms of tokenization on large rounds — too heavy for the main thread. A
  // dedicated worker owns the index: it builds off-thread, persists the
  // serialized index to IndexedDB per (round, user), and answers queries. The
  // main thread keeps only the (cheap) scoring/snippet logic. This mirrors the
  // funder workspace's search worker. While the worker is still building (cold
  // start), a synchronous fallback below serves searches so they're never
  // empty — the same behavior as before this worker existed.
  const roundId = round.id;
  // Scope the persisted index to the effective (impersonated) user so an
  // admin's index is never restored into an impersonated session.
  const cacheScope = userId ?? "anonymous";

  const workerRef = useRef<Worker | null>(null);
  const requestIdRef = useRef(0);
  const pendingSearchesRef = useRef(
    new Map<number, (hits: ProposalSearchHit[]) => void>(),
  );
  // False until this worker has been sent the corpus once. The first push goes
  // out immediately (not debounced) so a restored-from-cache index is reconciled
  // against the current corpus before the first search runs — see below.
  const hasPostedDocsRef = useRef(false);
  // init acked — the worker is alive and may be fed documents.
  const [isWorkerReady, setIsWorkerReady] = useState(false);
  // The index is queryable (a document set was applied, or a populated index
  // was restored from cache). Until then the synchronous fallback serves search.
  const [isSearchReady, setIsSearchReady] = useState(false);
  // Bumps on every applied document set, so an active query re-runs as the
  // corpus grows (field responses stream in after the first answer).
  const [indexEpoch, setIndexEpoch] = useState(0);
  // Worker matches tagged with the query they were computed for, so the
  // displayed rows/scores and the search-relevance sort never diverge.
  const [workerMatches, setWorkerMatches] = useState<{
    query: string;
    matches: Map<string, ProposalSearchMatch>;
  } | null>(null);
  // The last search matches we applied (per query), re-applied to the current
  // base to bridge the gap on a warm load between the cache being marked
  // queryable and the worker's first result landing (see the searchView
  // fallback below). Matches (not rows) so current filters/data still apply.
  const previousAppliedMatchesRef = useRef<{
    query: string;
    matches: Map<string, ProposalSearchMatch>;
  } | null>(null);

  useEffect(() => {
    const worker = new Worker(
      new URL("./proposal-search/proposalSearch.worker.ts", import.meta.url),
      { type: "module" },
    );
    attachWorkerErrorReporting(worker, "proposalSearch");
    workerRef.current = worker;

    worker.onmessage = (event: MessageEvent<ProposalSearchWorkerResponse>) => {
      const message = event.data;
      if (message.type === "status") {
        setIsWorkerReady(true);
        // A restored, populated index is queryable immediately; otherwise the
        // index becomes queryable once a document set has been applied.
        if (
          message.cause === "set-documents" ||
          (message.restoredFromCache && message.indexedCount > 0)
        ) {
          setIsSearchReady(true);
        }
        if (message.cause === "set-documents") {
          setIndexEpoch((epoch) => epoch + 1);
        }
      } else if (message.type === "search-results") {
        const resolve = pendingSearchesRef.current.get(message.requestId);
        if (resolve != null) {
          pendingSearchesRef.current.delete(message.requestId);
          resolve(message.hits);
        }
      } else {
        console.error("proposalSearch worker error:", message.message);
        reportWorkerProtocolError(
          "proposalSearch",
          message.message,
          message.stack,
        );
        // The worker doesn't say which request failed, so settle every
        // in-flight search with empty hits rather than leaving the displayed
        // results lagging the query indefinitely.
        for (const resolve of pendingSearchesRef.current.values()) {
          resolve([]);
        }
        pendingSearchesRef.current.clear();
      }
    };

    const initMessage: ProposalSearchWorkerRequest = {
      type: "init",
      roundId,
      cacheScope,
    };
    worker.postMessage(initMessage);

    const pendingSearches = pendingSearchesRef.current;
    return () => {
      worker.terminate();
      workerRef.current = null;
      pendingSearches.clear();
      hasPostedDocsRef.current = false;
      setIsWorkerReady(false);
      setIsSearchReady(false);
      setIndexEpoch(0);
      setWorkerMatches(null);
    };
  }, [roundId, cacheScope]);

  // Feed the worker the current corpus. The worker diffs by content version, so
  // a re-extraction only re-indexes the documents that actually changed (and
  // discards ones that vanished) — never a full rebuild.
  //
  // The FIRST push goes out immediately; later ones (from live edits) are
  // debounced. Sending it immediately matters on a warm load: the worker marks
  // a restored-from-cache index queryable right away, but that index can be
  // stale (a field answer edited since it was persisted). Because effects run
  // top-to-bottom and the worker processes messages FIFO, an immediate
  // set-documents is enqueued before the search effect's first query, so the
  // worker reconciles the corpus before answering — the first results are never
  // stale. A 200ms-debounced first push would let that query run against the
  // unreconciled index.
  useEffect(() => {
    if (!isWorkerReady) return;
    const postDocuments = () => {
      const worker = workerRef.current;
      if (worker == null) return;
      const message: ProposalSearchWorkerRequest = {
        type: "set-documents",
        documents: buildProposalSearchDocuments(
          allRows,
          fieldResponsesByProposalId,
          fieldTitlesByProposalId,
        ),
      };
      worker.postMessage(message);
      hasPostedDocsRef.current = true;
    };
    if (!hasPostedDocsRef.current) {
      postDocuments();
      return;
    }
    const timer = setTimeout(postDocuments, 200);
    return () => clearTimeout(timer);
  }, [
    isWorkerReady,
    allRows,
    fieldResponsesByProposalId,
    fieldTitlesByProposalId,
  ]);

  // Run the active query against the worker once the index is queryable.
  useEffect(() => {
    const worker = workerRef.current;
    const query = deferredSearchQuery.trim();
    if (worker == null || !isSearchReady || query === "") {
      // Drop stale matches when search clears so a fresh search never briefly
      // shows the previous query's results.
      setWorkerMatches(null);
      return;
    }
    let cancelled = false;
    const requestId = ++requestIdRef.current;
    void new Promise<ProposalSearchHit[]>((resolve) => {
      pendingSearchesRef.current.set(requestId, resolve);
      const message: ProposalSearchWorkerRequest = {
        type: "search",
        requestId,
        query: normalizeSearchText(query),
      };
      worker.postMessage(message);
    }).then((hits) => {
      if (cancelled) return;
      setWorkerMatches({
        query,
        matches: reduceProposalSearchResults(hits, query),
      });
    });
    return () => {
      cancelled = true;
      pendingSearchesRef.current.delete(requestId);
    };
  }, [deferredSearchQuery, isSearchReady, indexEpoch]);

  // Derive the displayed rows AND the query they reflect together, so the
  // search-relevance sort (which keys off `appliedSearchQuery`) can never
  // diverge from the rows/scores it sorts.
  const searchView = useMemo(() => {
    const base = filterRows(
      allRows,
      trackFilter,
      effectiveStatusFilter,
      effectiveShowHidden,
      "",
    );
    const query = deferredSearchQuery.trim();
    if (query === "") return { rows: base, query: "", matches: null };

    // Steady state: worker matches for exactly this query.
    if (workerMatches != null && workerMatches.query === query) {
      return {
        rows: applyProposalSearchMatches(base, workerMatches.matches),
        query,
        matches: workerMatches.matches,
      };
    }
    // Cold start: the worker has no queryable index yet. Search synchronously
    // so an early query is never empty — only reachable in this brief window.
    if (!isSearchReady) {
      const matches = searchProposals(
        buildProposalSearchIndex(
          allRows,
          fieldResponsesByProposalId,
          fieldTitlesByProposalId,
        ),
        query,
      );
      return {
        rows: applyProposalSearchMatches(base, matches),
        query,
        matches,
      };
    }
    // Worker is ready but hasn't answered THIS query yet (sub-frame lag). Keep
    // showing the last settled matches — tagged with their own query so sort
    // stays consistent — until the fresh answer arrives a moment later.
    if (workerMatches != null) {
      return {
        rows: applyProposalSearchMatches(base, workerMatches.matches),
        query: workerMatches.query,
        matches: workerMatches.matches,
      };
    }
    // Worker is ready (e.g. a warm cache was restored) but no worker result has
    // landed for this query yet, so there are no matches to show. If we computed
    // matches for this same query just before (the synchronous fallback ran
    // before the worker became ready, as on a warm load with a `q` in the URL),
    // re-apply them to the *current* base so a populated search doesn't briefly
    // show every proposal under the default sort. Re-applying to the fresh base
    // (rather than reusing the old rows) keeps the current track/status/hidden
    // filters and proposal data in effect; only the same-query match scores and
    // previews are reused, and the worker's reply replaces them moments later.
    const previousMatches = previousAppliedMatchesRef.current;
    if (previousMatches != null && previousMatches.query === query) {
      return {
        rows: applyProposalSearchMatches(base, previousMatches.matches),
        query,
        matches: previousMatches.matches,
      };
    }
    return { rows: base, query: "", matches: null };
  }, [
    allRows,
    trackFilter,
    effectiveStatusFilter,
    effectiveShowHidden,
    deferredSearchQuery,
    workerMatches,
    isSearchReady,
    fieldResponsesByProposalId,
    fieldTitlesByProposalId,
  ]);

  const filteredRows = searchView.rows;
  const appliedSearchQuery = searchView.query;

  // Remember the last matches we actually applied, so the searchView fallback
  // can bridge the warm-load gap for that query without flashing the full
  // unsearched list. Skipped when no search is applied so a cleared search
  // doesn't leave a stale match set to reuse.
  useEffect(() => {
    if (searchView.query !== "" && searchView.matches != null) {
      previousAppliedMatchesRef.current = {
        query: searchView.query,
        matches: searchView.matches,
      };
    }
  }, [searchView]);

  const progress: ProgressData = useMemo(
    () => ({
      completed: counts.evaluated,
      total: counts.total,
      milestoneLabel: undefined,
      targetPercent: undefined,
      targetLabel: undefined,
    }),
    [counts],
  );

  // A filter is "narrowing" only when it picks a strict subset of the options.
  // An empty selection OR a full selection both mean "no narrowing" — only
  // partial selections actually exclude rows.
  const trackNarrowing =
    trackFilter.length > 0 && trackFilter.length < ALL_TRACK_CATEGORIES.length;
  const statusNarrowing =
    effectiveStatusFilter.length > 0 &&
    effectiveStatusFilter.length < ALL_STATUSES.length;
  const hasActiveFilters =
    trackNarrowing ||
    statusNarrowing ||
    effectiveShowHidden ||
    searchQuery.length > 0;

  return {
    recommender,
    isObserver,
    allRows,
    filteredRows,
    counts,
    progress,
    trackFilter,
    setTrackFilter,
    statusFilter: effectiveStatusFilter,
    setStatusFilter,
    showHidden: effectiveShowHidden,
    setShowHidden,
    searchQuery,
    setSearchQuery,
    appliedSearchQuery,
    hasActiveFilters,
    clearFilters,
  };
}
